import axios from "axios";
import { devError, devLog, devWarn } from "../utils/devLog";

const CUSTOM_ORDER_OWNER_SESSION_STORAGE_PREFIX = "cc_custom_owner_session_";
const CUSTOM_ORDER_OWNER_SESSION_HEADER = "x-custom-order-session";

type CustomBriefingVideoRecord = {
  url: string;
  language: string;
  source: string;
  usedFallback: boolean;
  isLocaleSpecific: boolean;
  hasStoredValue: boolean;
  posterUrl?: string;
  captionsUrl?: string;
  transcriptUrl?: string;
};

export const resolveApiBaseUrl = (env: Record<string, unknown> = import.meta.env) => {
  if (Boolean(env.DEV)) return "/api-coldcase";

  const configuredBaseUrl = String(env.VITE_API_BASE_URL || env.VITE_API_URL || "").trim();
  if (configuredBaseUrl) return configuredBaseUrl;

  throw new Error("ColdClient production requires VITE_API_BASE_URL or VITE_API_URL.");
};

const baseURL = resolveApiBaseUrl();

const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

devLog("api", "client_initialized", {
  baseURL,
  isDev: import.meta.env.DEV,
});

const isCustomOrderAccessUrl = (value: unknown) => {
  const url = String(value || "").trim();
  return url.includes("/club/custom-orders") || url.includes("/club/custom-order-shares");
};

const buildCustomOrderOwnerSessionStorageKey = (accessToken: string) =>
  `${CUSTOM_ORDER_OWNER_SESSION_STORAGE_PREFIX}${String(accessToken || "").trim()}`;

const extractCustomOrderAccessTokenFromUrl = (value: unknown) => {
  const url = String(value || "").trim();
  const match = url.match(/\/club\/custom-orders\/([^/?#]+)/i);
  const accessToken = decodeURIComponent(String(match?.[1] || "").trim());
  if (!accessToken || accessToken.toLowerCase() === "access") return "";
  return accessToken;
};

export const getStoredCustomOrderOwnerSessionToken = (accessToken: string) => {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem(buildCustomOrderOwnerSessionStorageKey(accessToken)) || "").trim();
};

export const storeCustomOrderOwnerSessionToken = (accessToken: string, sessionToken: string) => {
  if (typeof window === "undefined") return;
  const normalizedAccessToken = String(accessToken || "").trim();
  const normalizedSessionToken = String(sessionToken || "").trim();
  if (!normalizedAccessToken || !normalizedSessionToken) return;
  window.localStorage.setItem(
    buildCustomOrderOwnerSessionStorageKey(normalizedAccessToken),
    normalizedSessionToken
  );
};

export const clearStoredCustomOrderOwnerSessionToken = (accessToken: string) => {
  if (typeof window === "undefined") return;
  const normalizedAccessToken = String(accessToken || "").trim();
  if (!normalizedAccessToken) return;
  window.localStorage.removeItem(buildCustomOrderOwnerSessionStorageKey(normalizedAccessToken));
};

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    (config as any).__startedAt = Date.now();
    const token = await window.Clerk?.session?.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const userId = window.Clerk?.user?.id;
    if (userId) {
      config.headers["x-user-id"] = userId;
    }
    const customOrderAccessToken = extractCustomOrderAccessTokenFromUrl(config.url);
    const customOrderSessionToken = customOrderAccessToken
      ? getStoredCustomOrderOwnerSessionToken(customOrderAccessToken)
      : "";
    if (customOrderSessionToken) {
      config.headers[CUSTOM_ORDER_OWNER_SESSION_HEADER] = customOrderSessionToken;
    }

    devLog("api", "request", {
      method: String(config.method || "get").toUpperCase(),
      url: config.url,
      baseURL: config.baseURL || baseURL,
      hasAuthToken: Boolean(token),
      hasUserId: Boolean(userId),
      hasCustomOrderSession: Boolean(customOrderSessionToken),
    });
  } catch (error) {
    devError("api", "request_interceptor_error", {
      message: (error as Error)?.message,
    });
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => {
    const startedAt = Number((response.config as any)?.__startedAt || 0);
    const durationMs = startedAt > 0 ? Date.now() - startedAt : undefined;
    devLog("api", "response", {
      method: String(response.config?.method || "get").toUpperCase(),
      url: response.config?.url,
      status: response.status,
      durationMs,
    });
    return response;
  },
  (error) => {
    const customOrderAccessToken = extractCustomOrderAccessTokenFromUrl(error.config?.url);
    const responseCode = String(error.response?.data?.code || "").trim();
    if (error.response?.status === 401 && responseCode === "owner_verification_required" && customOrderAccessToken) {
      clearStoredCustomOrderOwnerSessionToken(customOrderAccessToken);
    }
    devError("api", "response_error", {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      headers: error.config?.headers,
    });
    if (error.response?.status === 401 && !isCustomOrderAccessUrl(error.config?.url)) {
      // Handle unauthorized access
      devWarn("auth", "unauthorized_redirect", {
        redirectTo: "/agency/sign-in",
        sourceUrl: error.config?.url,
      });
      window.location.href = "/agency/sign-in";
    }
    return Promise.reject(error);
  }
);

export const StoryAPI = {
  getAll: async (params = {}) => {
    const { data } = await api.get("/stories", { params });
    return data;
  },
  getFeatured: async () => {
    const { data } = await api.get("/stories/featured");
    return data;
  },
  getById: async (id: string) => {
    const { data } = await api.get(`/stories/${id}`);
    return data;
  },
  getUserProfile: async () => {
    const { data } = await api.get("/users/me");
    return data;
  },
  getSponsoredStories: async () => {
    const { data } = await api.get("/users/me/sponsored");
    return data;
  },
};

export const UserAPI = {
  getProfile: async () => {
    const response = await api.get("/users/me");
    return response.data;
  },
  getCredits: async () => {
    const response = await api.get("/users/me/credits");
    return response.data;
  },
  getSponsoredStories: async () => {
    const response = await api.get("/users/me/sponsored");
    return response.data;
  },
  updateProfile: async (data: any) => {
    const response = await api.put("/users/me", data);
    return response.data;
  },
};

export const PurchaseAPI = {
  initPurchase: async (data: any) => {
    const response = await api.post("/purchases/init", data);
    return response.data;
  },
  completePurchase: async (data: any) => {
    const response = await api.post("/purchases/complete", data);
    return response.data;
  },
  getPurchases: async () => {
    const response = await api.get("/purchases/me");
    return response.data;
  },
};

export const FunnelAPI = {
  track: async (data: any) => {
    const response = await api.post("/funnel/track", data);
    return response.data;
  },
};

export const CustomOrderAPI = {
  bootstrap: async (data: {
    accessToken?: string;
    fastspringOrderId?: string;
    fastspringOrderReference?: string;
    email?: string;
    packageKey?: string;
    source?: string;
    language?: string;
    topic?: string;
    attributionSource?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    forceNew?: boolean;
    marketingOptIn?: boolean;
    marketingOptInSource?: string;
    policyAccepted?: boolean;
  }) => {
    const response = await api.post("/club/custom-orders/bootstrap", data);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        suspectCount: number;
      };
    };
  },
  getOrder: async (accessToken: string) => {
    const response = await api.get(`/club/custom-orders/${accessToken}`);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        maskedCustomerEmail?: string;
        policyAcknowledgedAt?: string | null;
        marketingOptIn?: boolean;
        marketingOptInAt?: string | null;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        suspectCount: number;
      };
    };
  },
  getAccessState: async (accessToken: string) => {
    const response = await api.get(`/club/custom-orders/${accessToken}/access-state`);
    return response.data as {
      ok: true;
      access: {
        ownerVerified: boolean;
        maskedEmail: string;
        marketingOptIn: boolean;
        policyAcknowledgedAt?: string | null;
        status: string;
        paymentConfirmed: boolean;
        resumeReady: boolean;
        shareLinks: Array<{
          id: string;
          label: string;
          mode: string;
          expiresAt?: string | null;
          createdAt?: string | null;
          accessCount: number;
          sharePath: string;
          shareUrl: string;
        }>;
      };
    };
  },
  requestAccessCode: async (data: { accessToken?: string; email?: string; packageKey?: string }) => {
    const response = await api.post("/club/custom-orders/access/request-code", data);
    return response.data as {
      ok: true;
      access: {
        maskedEmail: string;
        expiresAt?: string | null;
        devCodePreview?: string;
      };
    };
  },
  verifyAccessCode: async (data: {
    accessToken?: string;
    email?: string;
    packageKey?: string;
    code: string;
  }) => {
    const response = await api.post("/club/custom-orders/access/verify-code", data);
    return response.data as {
      ok: true;
      access: {
        accessToken: string;
        maskedEmail: string;
        ownerVerified: boolean;
        sessionToken?: string;
        sessionExpiresAt?: string | null;
      };
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
      };
    };
  },
  clearAccessSession: async (accessToken: string) => {
    const response = await api.delete(`/club/custom-orders/${accessToken}/access-session`);
    return response.data as { ok: true };
  },
  getDelivery: async (accessToken: string) => {
    const response = await api.get(`/club/custom-orders/${accessToken}/delivery`);
    return response.data as {
      ok: true;
      delivery: {
        available: boolean;
        status: string;
        customerFirstName?: string;
        title?: string;
        summary?: string;
        generatedCaseId?: string;
        renderStatus?: string;
        briefingVideoUrl?: string;
        briefingVideo?: CustomBriefingVideoRecord;
        heroImageUrl?: string;
        packetPdfUrl?: string;
        packetPdfExpiresAt?: number;
        caseAccessPath?: string;
        caseAccessUrl?: string;
        suspects?: Array<{
          name: string;
          relation?: string;
          occupation?: string;
          imageUrl?: string;
        }>;
        evidenceHighlights?: Array<{
          id: string;
          type: string;
          title: string;
          imageCount: number;
        }>;
      };
    };
  },
  getCaseAccess: async (accessToken: string) => {
    const response = await api.get(`/club/custom-orders/${accessToken}/case-access`);
    return response.data as {
      ok: true;
      caseAccess: {
        caseIndex?: number;
        available: boolean;
        status: string;
        customerFirstName?: string;
        title?: string;
        summary?: string;
        briefingTitle?: string;
        briefingBody?: string;
        generatedCaseId?: string;
        renderStatus?: string;
        briefingVideoUrl?: string;
        briefingVideo?: CustomBriefingVideoRecord;
        casePayload?: Record<string, unknown> | null;
        evidenceImageAccessMap?: Record<string, string>;
        suspectImageAccessMap?: Record<string, string>;
        heroImageUrl?: string;
        packetPdfUrl?: string;
        packetPdfExpiresAt?: number;
        caseAccessPath?: string;
        caseAccessUrl?: string;
        suspects?: Array<{
          name: string;
          relation?: string;
          occupation?: string;
          imageUrl?: string;
        }>;
        evidenceHighlights?: Array<{
          id: string;
          type: string;
          title: string;
          imageCount: number;
        }>;
        solveStatus?: {
          attemptCount?: number;
          hasSubmittedWarrant: boolean;
          selectedSuspectIndex: number | null;
          selectedSuspectName: string;
          isCorrect: boolean;
          solutionUnlocked: boolean;
          submittedAt?: string | null;
          solutionUnlockedAt?: string | null;
        };
        access?: {
          granted: boolean;
          mode: string;
        };
      };
    };
  },
  getSharedCaseAccess: async (shareToken: string) => {
    const response = await api.get(`/club/custom-order-shares/${shareToken}/case-access`);
    return response.data as {
      ok: true;
      caseAccess: {
        caseIndex?: number;
        available: boolean;
        status: string;
        title?: string;
        summary?: string;
        briefingTitle?: string;
        briefingBody?: string;
        generatedCaseId?: string;
        renderStatus?: string;
        briefingVideoUrl?: string;
        briefingVideo?: CustomBriefingVideoRecord;
        casePayload?: Record<string, unknown> | null;
        evidenceImageAccessMap?: Record<string, string>;
        suspectImageAccessMap?: Record<string, string>;
        heroImageUrl?: string;
        packetPdfUrl?: string;
        packetPdfExpiresAt?: number;
        caseAccessPath?: string;
        caseAccessUrl?: string;
        suspects?: Array<{
          name: string;
          relation?: string;
          occupation?: string;
          imageUrl?: string;
        }>;
        evidenceHighlights?: Array<{
          id: string;
          type: string;
          title: string;
          imageCount: number;
        }>;
        solveStatus?: {
          attemptCount?: number;
          hasSubmittedWarrant: boolean;
          selectedSuspectIndex: number | null;
          selectedSuspectName: string;
          isCorrect: boolean;
          solutionUnlocked: boolean;
          submittedAt?: string | null;
          solutionUnlockedAt?: string | null;
        };
        access?: {
          granted: boolean;
          mode: string;
        };
      };
      share: {
        id: string;
        label: string;
        mode: string;
        expiresAt?: string | null;
      };
    };
  },
  downloadCasePdf: async (
    accessToken: string,
    payload: { theme: "light" | "dark"; evidenceIds: string[] }
  ) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/pdf`, payload, {
      responseType: "blob",
    });
    return response;
  },
  submitWarrant: async (accessToken: string, payload: { suspectIndex: number }) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/warrant`, payload);
    return response.data as {
      ok: boolean;
      created: boolean;
      warrant: {
        selectedSuspectIndex: number;
        selectedSuspectName: string;
        isCorrect: boolean;
      };
      solveStatus: {
        attemptCount?: number;
        hasSubmittedWarrant: boolean;
        selectedSuspectIndex: number | null;
        selectedSuspectName: string;
        isCorrect: boolean;
        solutionUnlocked: boolean;
        submittedAt?: string | null;
        solutionUnlockedAt?: string | null;
      };
    };
  },
  submitSharedWarrant: async (shareToken: string, payload: { suspectIndex: number }) => {
    const response = await api.post(`/club/custom-order-shares/${shareToken}/warrant`, payload);
    return response.data as {
      ok: boolean;
      created: boolean;
      warrant: {
        selectedSuspectIndex: number;
        selectedSuspectName: string;
        isCorrect: boolean;
      };
      solveStatus: {
        attemptCount?: number;
        hasSubmittedWarrant: boolean;
        selectedSuspectIndex: number | null;
        selectedSuspectName: string;
        isCorrect: boolean;
        solutionUnlocked: boolean;
        submittedAt?: string | null;
        solutionUnlockedAt?: string | null;
      };
    };
  },
  devResetWarrant: async (accessToken: string) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/dev-reset-warrant`);
    return response.data as {
      ok: boolean;
      solveStatus: {
        attemptCount?: number;
        hasSubmittedWarrant: boolean;
        selectedSuspectIndex: number | null;
        selectedSuspectName: string;
        isCorrect: boolean;
        solutionUnlocked: boolean;
        submittedAt?: string | null;
        solutionUnlockedAt?: string | null;
      };
    };
  },
  devResetSharedWarrant: async (shareToken: string) => {
    const response = await api.post(`/club/custom-order-shares/${shareToken}/dev-reset-warrant`);
    return response.data as {
      ok: boolean;
      solveStatus: {
        attemptCount?: number;
        hasSubmittedWarrant: boolean;
        selectedSuspectIndex: number | null;
        selectedSuspectName: string;
        isCorrect: boolean;
        solutionUnlocked: boolean;
        submittedAt?: string | null;
        solutionUnlockedAt?: string | null;
      };
    };
  },
  listShareLinks: async (accessToken: string) => {
    const response = await api.get(`/club/custom-orders/${accessToken}/share-links`);
    return response.data as {
      ok: true;
      shareLinks: Array<{
        id: string;
        label: string;
        mode: string;
        expiresAt?: string | null;
        createdAt?: string | null;
        accessCount: number;
        sharePath: string;
        shareUrl: string;
      }>;
    };
  },
  createShareLink: async (
    accessToken: string,
    payload: { label?: string; mode?: "viewer"; expiresInHours?: number } = {}
  ) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/share-links`, payload);
    return response.data as {
      ok: true;
      shareLink: {
        id: string;
        label: string;
        mode: string;
        expiresAt?: string | null;
        sharePath: string;
        shareUrl: string;
      };
      shareLinks: Array<{
        id: string;
        label: string;
        mode: string;
        expiresAt?: string | null;
        createdAt?: string | null;
        accessCount: number;
        sharePath: string;
        shareUrl: string;
      }>;
    };
  },
  revokeShareLink: async (accessToken: string, shareLinkId: string) => {
    const response = await api.delete(`/club/custom-orders/${accessToken}/share-links/${shareLinkId}`);
    return response.data as {
      ok: true;
      shareLinks: Array<{
        id: string;
        label: string;
        mode: string;
        expiresAt?: string | null;
        createdAt?: string | null;
        accessCount: number;
        sharePath: string;
        shareUrl: string;
      }>;
    };
  },
  saveIntakeDraft: async (accessToken: string, data: Record<string, unknown>) => {
    const response = await api.put(`/club/custom-orders/${accessToken}/intake`, data);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        suspectCount: number;
      };
    };
  },
  submitIntake: async (accessToken: string) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/submit`);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        canSelectIdea?: boolean;
        suspectCount: number;
      };
    };
  },
  confirmDevPayment: async (accessToken: string) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/dev-confirm-payment`);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        canSelectIdea?: boolean;
        suspectCount: number;
      };
    };
  },
  requestMoreIdeas: async (accessToken: string) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/more-ideas`);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        canSelectIdea?: boolean;
        suspectCount: number;
      };
    };
  },
  selectIdea: async (accessToken: string, ideaId: string) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/select-idea`, { ideaId });
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        canSelectIdea?: boolean;
        suspectCount: number;
      };
      queue: {
        runId: string;
        caseId: string;
        status: string;
        webhookTriggered: boolean;
        message: string;
      };
    };
  },
  restartGeneration: async (accessToken: string) => {
    const response = await api.post(`/club/custom-orders/${accessToken}/restart-generation`);
    return response.data as {
      ok: true;
      order: {
        id: string;
        accessToken: string;
        status: string;
        customerEmail: string;
        paymentConfirmedAt?: string | null;
        ideasGeneratedAt?: string | null;
        selectedIdeaAt?: string | null;
        intakeDraft: Record<string, unknown>;
        generatedIdeas?: Array<Record<string, unknown>>;
        selectedIdea?: Record<string, unknown> | null;
        generationConfig?: Record<string, unknown>;
        generatedCaseId?: string;
        generationRunId?: string;
        canSelectIdea?: boolean;
        suspectCount: number;
      };
      queue: {
        runId: string;
        caseId: string;
        status: string;
        webhookTriggered: boolean;
        message: string;
      };
    };
  },
};

export const AdminAPI = {
  getDashboard: async () => {
    const response = await api.get("/admin/dashboard");
    return response.data;
  },
  getStories: async (params: any) => {
    const response = await api.get("/admin/stories", { params });
    return response.data;
  },
  getUserCredits: async (params: any) => {
    const response = await api.get("/admin/user-credits", { params });
    return response.data;
  },
  getFunnelAnalytics: async (params: any) => {
    const response = await api.get("/funnel/analytics", { params });
    return response.data;
  },
  getFunnelSessions: async (params: any) => {
    const response = await api.get("/funnel/sessions", { params });
    return response.data;
  },
};

export { api };
